import { useTranslation } from 'react-i18next';
import {
  QuestionAnnouncer as AskingAnnouncer,
  QuestionPrompt as QuestionCard,
} from '@inference.engine/agentic-ui/components';
import { AgenticUiText } from '@/shared/i18n/AgenticUiText';
import type { PauseDecisions, PausedQuestion } from '../model/usePauseDecisions';

export interface QuestionPromptProps {
  /** The paused turn's decisions, shared with the approval card in the thread. */
  pause: PauseDecisions;
}

/**
 * The questions a paused turn asks, answered inside the chat input where suggestions show, in the
 * same card the builder's test chat draws. A turn may ask more than one question tool call at once;
 * each gets its own card. The pause keeps the drafts, so a failed send brings the answers back.
 */
export function QuestionPrompt({ pause }: QuestionPromptProps) {
  if (pause.questions.length === 0) return null;
  return (
    <AgenticUiText>
      <div className="flex flex-col">
        {pause.questions.map((paused) => (
          <QuestionsOfCall key={paused.approval.approvalId} paused={paused} pause={pause} />
        ))}
      </div>
    </AgenticUiText>
  );
}

/**
 * Says that the agent is asking, for someone who cannot see the questions appear. Kept mounted so
 * the change is announced: the questions arrive after the turn ends, away from the message box.
 */
export function QuestionAnnouncer({ pause }: QuestionPromptProps) {
  return (
    <AgenticUiText>
      <AskingAnnouncer waiting={pause.waitingOn === 'questions'} />
    </AgenticUiText>
  );
}

function QuestionsOfCall({ paused, pause }: { paused: PausedQuestion; pause: PauseDecisions }) {
  const { t } = useTranslation('chats');
  const { approval, questions } = paused;
  const { approvalId } = approval;
  const decided = pause.decided[approvalId] !== undefined;

  return (
    <QuestionCard
      questions={questions}
      drafts={pause.draftsOf(approvalId)}
      onDraftChange={(index, draft) => {
        pause.setDraft(approvalId, index, draft);
      }}
      disabled={pause.sending || decided}
      label={
        approval.agentName
          ? t('toolApprovals.question.label', { agent: approval.agentName })
          : t('toolApprovals.question.labelNoAgent')
      }
      note={
        pause.failed ? (
          <span className="text-destructive">{t('toolApprovals.question.failed')}</span>
        ) : decided && pause.waitingOn === 'approvals' ? (
          t('toolApprovals.question.waitingForApproval')
        ) : undefined
      }
      onSubmit={(answers) => {
        pause.decide({ approvalId, approved: true, answers });
      }}
      onSkip={() => {
        pause.decide({ approvalId, approved: false });
      }}
    />
  );
}
